import { DueDiligencePageGetDueDiligenceRecordQuery } from '@/__generated__/graphql';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { getCacheBustedFileUrl } from '@/components/settings-page/utils';
import { isValidUrl } from '@/shared/utils/url';
import { Document, Page, Image as PdfImage, StyleSheet, Text, View } from '@react-pdf/renderer';
import { DefaultTheme } from 'styled-components/dist/types';
import { convertDueDiligenceFieldToFieldEditor } from '../../fieldList';
import { PDFSectionScore } from './components/PDFSectionScore';
import { PDFTemplateFooter } from './components/PDFTemplateFooter';
import { PDFTemplateHeader } from './components/PDFTemplateHeader';
import { OneColumnSection } from './components/sections/OneColumnSection';
import { ThreeColumnSection } from './components/sections/ThreeColumnSection';
import { TwoColumnSection } from './components/sections/TwoColumnSection';
import { templateStyles } from './style';

interface DueDiligenceRecordPDFTemplateProps {
  dueDiligenceRecordTier: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number];
  investmentName: string;
  investmentDescription: string;
  firmInfoData: FirmInfoType;
  templateStylingData: ITemplateStylingSettings;
  includeAiSuggested: boolean;
  theme: DefaultTheme;
}

export const DueDiligenceRecordPDFTemplate = ({
  dueDiligenceRecordTier,
  investmentName,
  investmentDescription,
  firmInfoData,
  templateStylingData,
  includeAiSuggested,
  theme
}: DueDiligenceRecordPDFTemplateProps) => {
  const totalScore = dueDiligenceRecordTier?.sections?.reduce((accumulator, currentValue) => accumulator + currentValue.data.score, 0) ?? 0;

  const styles = StyleSheet.create({
    sectionName: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '20px',
      fontFamily: templateStylingData?.headerFontStyle?.fontFamily || 'Roboto Slab',
      color: templateStylingData?.headerFontStyle?.fontColor ? `#${templateStylingData?.headerFontStyle?.fontColor}` : theme.font.strong,
      fontWeight: Number(templateStylingData?.headerFontStyle?.fontWeight) || 400,
      paddingBottom: '4px',
      borderBottom: `2px solid ${theme.layer[2]}`,
      marginBottom: '10px'
    }
  });

  const getSection = (
    section: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]
  ) => {
    const fields = convertDueDiligenceFieldToFieldEditor(section?.fields || [], null);

    switch (section.pdfLayout) {
      case 'THREE_COLUMN':
        return (
          <View style={{ marginBottom: '30px' }} key={section.data.id}>
            <View style={styles.sectionName}>
              <Text>{section.name}</Text>
              {section.userCanScore ? <PDFSectionScore score={section.data.score} templateStylingData={templateStylingData} theme={theme} /> : null}
            </View>
            <ThreeColumnSection fields={fields} templateStylingData={templateStylingData} includeAiSuggested={includeAiSuggested} theme={theme} />
          </View>
        );
      case 'TWO_COLUMN_GRID':
        return (
          <View break={true} style={{ marginBottom: '30px' }} key={section.data.id}>
            <View style={styles.sectionName}>
              <Text>{section.name}</Text>
              {section.userCanScore ? <PDFSectionScore score={section.data.score} templateStylingData={templateStylingData} theme={theme} /> : null}
            </View>
            <TwoColumnSection fields={fields} templateStylingData={templateStylingData} includeAiSuggested={includeAiSuggested} theme={theme} />
          </View>
        );
      default:
        const shouldShowSection = section.fields.some(
          (field) =>
            (field.data.value && field.data.value !== field.AI?.answers[0]) ||
            (field.AI?.answers.length && includeAiSuggested && (field.data.value === null || field.data.value === field.AI?.answers[0])) ||
            field.data.attachments.length > 0
        );

        if (!shouldShowSection) return null;

        return (
          <View style={{ marginBottom: '30px' }} key={section.data.id}>
            <View style={styles.sectionName}>
              <Text>{section.name}</Text>
              {section.userCanScore ? <PDFSectionScore score={section.data.score} templateStylingData={templateStylingData} theme={theme} /> : null}
            </View>
            <OneColumnSection fields={fields} templateStylingData={templateStylingData} includeAiSuggested={includeAiSuggested} theme={theme} />
          </View>
        );
    }
  };

  if (!dueDiligenceRecordTier) return <></>;

  return (
    <Document>
      <Page size="A4" style={templateStyles.page}>
        <PDFTemplateHeader templateStylingData={templateStylingData} theme={theme} />
        {templateStylingData?.watermark !== 'None' && isValidUrl(templateStylingData?.watermark) ? (
          <View style={templateStyles.watermarkWrap} fixed>
            <PdfImage style={templateStyles.watermark} src={getCacheBustedFileUrl(templateStylingData?.watermark)} />
          </View>
        ) : null}
        {templateStylingData?.watermark !== 'None' && !isValidUrl(templateStylingData?.watermark) ? (
          <View
            style={{
              position: 'absolute',
              bottom: templateStylingData?.watermark?.length > 15 ? '250px' : '400px',
              left: '50px',
              opacity: 0.1
            }}
            fixed
          >
            <Text style={templateStyles.watermarkText}>{templateStylingData?.watermark}</Text>
          </View>
        ) : null}
        <View style={templateStyles.contentWrap}>
          <View style={templateStyles.header}>
            <View style={templateStyles.titleWrap}>
              <View
                style={{
                  ...templateStyles.investmentName,
                  fontFamily: templateStylingData?.headerFontStyle?.fontFamily || 'Roboto Slab',
                  color: templateStylingData?.headerFontStyle?.fontColor ? `#${templateStylingData?.headerFontStyle?.fontColor}` : theme.font.strong,
                  width: dueDiligenceRecordTier?.sections?.filter((section) => section.userCanScore).length ? '70%' : '100%'
                }}
              >
                <Text>{investmentName}</Text>
              </View>
              {dueDiligenceRecordTier?.sections?.filter((section) => section.userCanScore).length ? (
                <>
                  {totalScore > 0 ? (
                    <View
                      style={{
                        ...templateStyles.totalScoreWrap,
                        backgroundColor: theme.context.success,
                        color: theme.context.light
                      }}
                    >
                      <Text>Total score </Text>
                      <Text style={templateStyles.totalScore}>{`+${totalScore}`}</Text>
                    </View>
                  ) : null}
                  {totalScore === 0 ? (
                    <View
                      style={{
                        ...templateStyles.totalScoreWrap,
                        backgroundColor: '#F4F5F6',
                        color: '#474747',
                        border: '1px solid #474747'
                      }}
                    >
                      <Text>Total score </Text>
                      <Text style={templateStyles.totalScore}>{totalScore}</Text>
                    </View>
                  ) : null}
                  {totalScore < 0 ? (
                    <View
                      style={{
                        ...templateStyles.totalScoreWrap,
                        backgroundColor: theme.context.error,
                        color: theme.context.light
                      }}
                    >
                      <Text>Total score </Text>
                      <Text style={templateStyles.totalScore}>{totalScore}</Text>
                    </View>
                  ) : null}
                </>
              ) : (
                <></>
              )}
            </View>
            {dueDiligenceRecordTier?.name ? (
              <Text style={{ ...templateStyles.templateName, color: theme.font.base }}>{dueDiligenceRecordTier?.name ?? ''}</Text>
            ) : null}
            {investmentDescription ? (
              <Text style={{ ...templateStyles.investmentDescription, color: theme.font.base }}>{investmentDescription}</Text>
            ) : null}
          </View>
          {dueDiligenceRecordTier?.sections?.map((section) => getSection(section))}
        </View>
        <PDFTemplateFooter firmInfoData={firmInfoData} theme={theme} />
      </Page>
    </Document>
  );
};
